import axios from 'axios';
import { showToast } from '@/helper/toast.helper';
import { getServiceUrl } from '@/helper/apiConfig';
import { tokenService } from './tokenService';

// Global variable to store session modal trigger function
let triggerSessionModal: (() => void) | null = null;
let activityCallback: (() => void) | null = null;

export const setSessionModalTrigger = (trigger: () => void) => {
  triggerSessionModal = trigger;
};

export const setActivityCallback = (callback: () => void) => {
  activityCallback = callback;
};

export const USER_API_URL = `${getServiceUrl('users')}/api/v1`;
export const PROJECT_API_URL = `${getServiceUrl('surveys')}/api/v1`;
export const NOTIFICATION_API_URL = `${getServiceUrl('notification')}/api/v1`;
export const FAQ_API_URL = `${getServiceUrl('faq')}/api/v1`;
export const WALLET_API_URL = `${getServiceUrl('wallet')}/api/v1`;
export const DASHBOARD_API_URL = `${getServiceUrl('dashboard')}/api/v1`;

const getToken = async () => {
  return await tokenService.getValidToken();
};

const createApi = (baseURL: string) => {
  const instance = axios.create({
    baseURL,
    timeout: 60000, // Increased to 60 seconds
  });
  instance.interceptors.request.use(
    async (config) => {
      // Track API activity
      if (activityCallback) {
        activityCallback();
      }

      const token = await getToken();
      const finalToken = token;

      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('Authorization', finalToken ? `Bearer ${finalToken}` : '');
      } else {
        (config.headers as any) = {
          ...(config.headers || {}),
          Authorization: finalToken ? `Bearer ${finalToken}` : '',
        };
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        showToast.error('Request timed out. Please try again.');
        return Promise.reject(error);
      }

      const status = error.response?.status;

      // 401 — try token refresh, then session modal
      if (status === 401) {
        const refreshedToken = await tokenService.refreshAccessToken();

        if (refreshedToken && error.config) {
          error.config.headers.Authorization = `Bearer ${refreshedToken}`;
          return instance.request(error.config);
        }

        if (triggerSessionModal) {
          triggerSessionModal();
        } else {
          localStorage.clear();
          window.location.href = '/session-timeout';
        }

        return Promise.reject(error);
      }

      // 500+ server errors — show toast
      if (status && status >= 500) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Server error. Please try again later.';

        showToast.error(
          typeof message === 'string' ? message : 'Server error. Please try again later.',
        );
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

export const userApi = createApi(USER_API_URL);
export const projectApi = createApi(PROJECT_API_URL);
export const notificationApi = createApi(NOTIFICATION_API_URL);
export const faqApi = createApi(FAQ_API_URL);
export const walletApi = createApi(WALLET_API_URL);
export const dashboardApi = createApi(DASHBOARD_API_URL);
export default {
  userApi,
  projectApi,
  faqApi,
  walletApi,
  notificationApi,
  dashboardApi,
};
